import detectMobileDevice from 'is-mobile';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLauncher } from '../../hooks';
import { useStores } from '../../stores';
import {
  LAUNCHER_VARIANT_FLOATING,
  LAUNCHER_VARIANT_HIDDEN,
  LAUNCHER_VARIANT_ICON,
  LAUNCHER_VARIANT_SIDETAB
} from '../../utilities';
import FloatingButton from './FloatingButton';
import SideTab from './SideTab';
import VaIcon from './VaIcon';

const Launcher = () => {
  const {
    config: { fontFamily, startButton },
    chatWindow
  } = useStores();
  const { ready } = useTranslation('translation');
  const { t } = useTranslation();
  const isMobile = detectMobileDevice();
  const isMobileTablet = detectMobileDevice({ tablet: true });
  const { x, y, toggleOpen, toggleMinimize } = useLauncher({
    size: isMobileTablet && startButton.imgMobile ? startButton.sizeMobile : startButton.size,
    offset:
      isMobileTablet && startButton.mobilePosition
        ? startButton.mobilePosition.offset
        : startButton.offset,
    position:
      isMobileTablet && startButton.mobilePosition
        ? startButton.mobilePosition.position
        : startButton.position
  });

  if (!ready || chatWindow.isEmbedded || startButton.variant === LAUNCHER_VARIANT_HIDDEN) {
    return null;
  }

  if (
    (chatWindow.isMinimized && startButton.variant === LAUNCHER_VARIANT_FLOATING) ||
    (!chatWindow.isOpened && startButton.variant === LAUNCHER_VARIANT_FLOATING && isMobile) ||
    (!chatWindow.isOpened && startButton.variant === LAUNCHER_VARIANT_SIDETAB)
  ) {
    return (
      <SideTab
        title={startButton.minimizedView.title || startButton.title.content}
        onClick={chatWindow.isMinimized ? toggleMinimize : toggleOpen}
        align="right"
        padding={startButton.minimizedView.padding}
        background={startButton.minimizedView.background}
        color={startButton.minimizedView.color}
        textFontFamily={fontFamily}
        isSidetabOnInit={startButton.variant === LAUNCHER_VARIANT_SIDETAB}
        sidetabParameters={startButton.sidetab || {}}
      />
    );
  }

  if (!chatWindow.isOpened && startButton.variant === LAUNCHER_VARIANT_FLOATING && !isMobile) {
    return (
      <FloatingButton
        alternative={startButton.alternative}
        avatarEnabled={startButton.img.enabled}
        img={startButton.img}
        title={startButton.title}
        size={startButton.size}
        textarea={startButton.textarea}
        position={{ x, y }}
        onFocus={toggleOpen}
        onClick={toggleOpen}
        textFontFamily={fontFamily}
      />
    );
  }

  if (
    startButton.variant === LAUNCHER_VARIANT_ICON ||
    (chatWindow.isMinimized && startButton.variant === LAUNCHER_VARIANT_ICON)
  ) {
    return (
      <VaIcon
        position={{ x, y }}
        size={isMobileTablet && startButton.imgMobile ? startButton.sizeMobile : startButton.size}
        textFontFamily={fontFamily}
        onClick={toggleOpen}
        img={isMobileTablet && startButton.imgMobile ? startButton.imgMobile : startButton.img}
        icon={startButton.icon}
        isAnimation={startButton.icon.animation ? startButton.icon.animation.enabled : false}
        isOpened={!chatWindow.isClosed}
        isMinimized={chatWindow.isMinimized}
        isHidden={startButton.variant === LAUNCHER_VARIANT_HIDDEN}
        isSpeechBubble={startButton.speechBubble ? startButton.speechBubble.enabled : false}
        speechBubbleText={
          t('speechBubble') === 'speechBubble' ? 'Hi, chat with me!' : t('speechBubble')
        }
        roundedBorder={
          typeof startButton.icon.roundedBorder === 'undefined'
            ? true
            : startButton.icon.roundedBorder
        }
      />
    );
  }

  return null;
};

export default observer(Launcher);
